import { Link } from "react-router";

import {
  ExternalLinkIcon,
  MedalIcon,
  SwordsIcon,
  VideoIcon,
} from "lucide-react";

import {
  DetailField,
  DetailGrid,
  DetailSection,
  fmtDate,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type MatchSides,
  sideName,
} from "@/pages/matches/components/details/sides";
import { formatScore } from "@/pages/matches/lib/match-format";
import { TeamLink } from "@/pages/sports/components/team-link";
import { fmtCount, safeHttpUrl } from "@/pages/sports/lib/format";
import type {
  IHeadToHead,
  IHighlightClip,
  ITopPerformer,
} from "@/types/match-details";

const num = "text-right tabular-nums";

/** Football only; at most three, best match rating first. */
export function TopPerformersCard({
  performers,
  sides,
}: {
  performers: ITopPerformer[];
  sides: MatchSides;
}) {
  return (
    <DetailSection
      title="Top performers"
      icon={<MedalIcon className="size-4" />}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Player</TableHead>
            <TableHead>Team</TableHead>
            <TableHead className={num}>Rating</TableHead>
            <TableHead className={num}>Goals</TableHead>
            <TableHead className={num}>Assists</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {performers.map((p, i) => (
            <TableRow key={p.player?.providerId ?? i}>
              <TableCell>{p.player?.name ?? "—"}</TableCell>
              <TableCell>{sideName(sides, p.teamId) ?? "—"}</TableCell>
              <TableCell className={`${num} font-semibold`}>
                {typeof p.rating === "number" ? p.rating.toFixed(1) : "—"}
              </TableCell>
              <TableCell className={num}>{fmtCount(p.goals)}</TableCell>
              <TableCell className={num}>{fmtCount(p.assists)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DetailSection>
  );
}

/** A meeting's score, or its state when it has none (postponed, cancelled, not played yet). */
const meetingResult = (m: NonNullable<IHeadToHead["matches"]>[number]) => {
  const score = formatScore(m.homeScore, m.awayScore);
  return score === "—" && m.state && m.state !== "Finished" ? m.state : score;
};

/**
 * The last meetings of the two teams. The counts are relative to THIS match's home team.
 * Most meetings are in competitions or matches Mood never held, so the teams and league are
 * copies: a link when the copy carries an id, plain text when the league is not a Mood one.
 */
export function HeadToHeadCard({
  h2h,
  sides,
}: {
  h2h: IHeadToHead;
  sides: MatchSides;
}) {
  const meetings = h2h.matches ?? [];
  return (
    <DetailSection
      title="Head-to-head"
      icon={<SwordsIcon className="size-4" />}
    >
      <div className="flex flex-col gap-4">
        <DetailGrid className="sm:grid-cols-4">
          <DetailField label="Played" value={fmtCount(h2h.played)} />
          <DetailField
            label={`${sides.homeName ?? "Home"} wins`}
            value={fmtCount(h2h.homeTeamWins)}
          />
          <DetailField label="Draws" value={fmtCount(h2h.draws)} />
          <DetailField
            label={`${sides.awayName ?? "Away"} wins`}
            value={fmtCount(h2h.awayTeamWins)}
          />
        </DetailGrid>
        {meetings.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Home</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead>Away</TableHead>
                <TableHead>League</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {meetings.map((m, i) => {
                const league = m.league?.name ?? m.leagueName;
                return (
                  <TableRow key={i}>
                    <TableCell className="whitespace-nowrap">
                      {fmtDate(m.date)}
                    </TableCell>
                    <TableCell>
                      <TeamLink team={m.homeTeam} />
                    </TableCell>
                    <TableCell className="text-center font-medium tabular-nums">
                      {meetingResult(m)}
                    </TableCell>
                    <TableCell>
                      <TeamLink team={m.awayTeam} />
                    </TableCell>
                    <TableCell>
                      {league && m.league?.id ? (
                        <Link
                          to={`/leagues/show/${m.league.id}`}
                          className="hover:underline"
                        >
                          {league}
                        </Link>
                      ) : (
                        (league ?? "—")
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </DetailSection>
  );
}

/**
 * Up to three clips: title and source, each opened in a new tab. IMS never embeds them: the
 * backend only guarantees the URLs are absolute http(s), not that the host is trusted.
 */
export function HighlightsCard({ clips }: { clips: IHighlightClip[] }) {
  return (
    <DetailSection title="Highlights" icon={<VideoIcon className="size-4" />}>
      <ul className="flex flex-col gap-2">
        {clips.map((clip, i) => {
          const href = safeHttpUrl(clip.url);
          const title = clip.title ?? href ?? "Untitled clip";
          return (
            <li
              key={clip.url ?? i}
              className="flex flex-wrap items-center gap-2 text-sm"
            >
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:underline"
                >
                  {title}
                  <ExternalLinkIcon className="size-3.5 text-muted-foreground" />
                </a>
              ) : (
                <span>{title}</span>
              )}
              {clip.source && <Badge variant="outline">{clip.source}</Badge>}
              {clip.channel && (
                <span className="text-xs text-muted-foreground">
                  {clip.channel}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </DetailSection>
  );
}
